export class AIToolError extends Error {
  readonly userMessage: string;
  readonly suggestions?: string[];
  readonly context?: Record<string, unknown>;

  constructor(
    message: string,
    userMessage: string,
    suggestions?: string[],
    context?: Record<string, unknown>
  ) {
    super(message);
    this.name = "AIToolError";
    this.userMessage = userMessage;
    this.suggestions = suggestions;
    this.context = context;
  }

  static formatForUser(error: AIToolError): string {
    let message = `🚫 ${error.userMessage}`;

    if (error.suggestions?.length) {
      message += `\n\nSuggestions:\n${error.suggestions.map((s) => `• ${s}`).join("\n")}`;
    }

    return `\`\`\`error\n${message}\n\`\`\``;
  }
}

export class NoTransactionsError extends AIToolError {
  constructor(
    address: string,
    timeRange: {
      start: string;
      end: string;
      description?: string;
      numTransactions?: number;
    }
  ) {
    const userMessage = `No transactions found for address ${address} between ${new Date(timeRange.start).toLocaleString()} and ${new Date(timeRange.end).toLocaleString()}${timeRange.numTransactions ? ` (requested: ${timeRange.numTransactions} transactions)` : ""}`;

    super(
      "No transactions in time range",
      userMessage,
      [
        "Try expanding your time range",
        "Verify the address is correct",
        "Check if the address has any transactions on this chain",
      ],
      { address, timeRange }
    );
  }
}

export class TimeParseError extends AIToolError {
  constructor(timeDescription: string) {
    const userMessage = `Unable to understand the time range: "${timeDescription}"`;

    super(
      "Failed to parse time description",
      userMessage,
      [
        'Try using a specific format like "between 2023-01-15 and 2023-01-19"',
        'Or use relative time like "last 24 hours" or "older than 5 days"',
        'You can also use simple terms like "yesterday" or "last week"',
      ],
      { timeDescription }
    );
  }
}

export class ExplorerAPIError extends AIToolError {
  constructor(statusCode: number, statusText: string, url: string) {
    const userMessage = `The blockchain explorer is currently unavailable (${statusCode} ${statusText})`;

    super(
      `Explorer API error: ${statusText}`,
      userMessage,
      ["Please try again in a few moments"],
      {
        statusCode,
        statusText,
        url,
      }
    );
  }
}

export class ChainConfigError extends AIToolError {
  constructor(chainId: string, availableChains: string[]) {
    const userMessage = `Chain ID "${chainId}" is not supported`;

    super(
      "Chain configuration not found",
      userMessage,
      [
        `Available chains: ${availableChains.join(", ")}`,
        "Please use one of the supported chain IDs",
      ],
      { chainId, availableChains }
    );
  }
}

export class TransactionNotFoundError extends AIToolError {
  constructor(txHash: string, chainId: string) {
    const userMessage = `Transaction ${txHash} was not found on chain ${chainId}`;

    super(
      "Transaction not found",
      userMessage,
      [
        "Verify the transaction hash is correct",
        "Check if you're querying the right chain",
        "Wait a few moments if the transaction is recent",
      ],
      { txHash, chainId }
    );
  }
}

export class InvalidTransactionHashError extends AIToolError {
  constructor(txHash: string) {
    const userMessage = `Invalid transaction hash format: ${txHash}`;

    super(
      "Invalid transaction hash",
      userMessage,
      [
        "Transaction hash should be a 66-character hexadecimal string starting with 0x",
        "Please verify the transaction hash and try again",
      ],
      { txHash }
    );
  }
}

/**
 * Constant identifier for BlockScout rate limit errors.
 * Used for reliable client-side detection across the server/client boundary.
 */
export const BLOCKSCOUT_RATE_LIMIT_ERROR_TYPE =
  "BLOCKSCOUT_RATE_LIMIT" as const;

export class BlockScoutRateLimitError extends AIToolError {
  readonly statusCode: number;
  readonly isQuotaError: boolean;

  constructor(statusCode: number, url?: string) {
    const isQuotaError = statusCode === 402 || statusCode === 403;
    const userMessage = isQuotaError
      ? "BlockScout API quota exceeded. The free tier limit has been reached."
      : "BlockScout API rate limit reached. Too many requests in a short time.";

    super(
      `BlockScout API ${isQuotaError ? "quota exceeded" : "rate limit"}: ${statusCode}`,
      userMessage,
      [
        "Please try again in a few moments",
        "Consider tweeting support to help us get sponsored API access",
      ],
      {
        statusCode,
        url,
        isQuotaError,
        // This constant allows reliable detection on the client side
        errorType: BLOCKSCOUT_RATE_LIMIT_ERROR_TYPE,
      }
    );

    // Override the name so we can detect this specific error type after serialization
    this.name = "BlockScoutRateLimitError";
    this.statusCode = statusCode;
    this.isQuotaError = isQuotaError;
  }

  static isRateLimitStatusCode(statusCode: number): boolean {
    return statusCode === 429 || statusCode === 402 || statusCode === 403;
  }
}
